import React, { useEffect, useMemo, useState } from 'react';
import s from './NovaNews.module.sass';
import { useAppContext } from '../../app-core/AppContext';
import { Newspaper, ExternalLink } from 'lucide-react';
import useFitGrid from './useFitGrid';

// Геометрия ячейки новости — база расчёта адаптивной сетки (см. useFitGrid).
// Пороги подобраны на плотность: чем меньше, тем больше колонок/рядов влезает
const ROW_H = 56;      // высота ряда (лого + 2 строки заголовка + мета)
const ROW_GAP = 10;
const MIN_COL_W = 230; // уже — заголовки не читаются; шире экран → больше колонок
const COL_GAP = 16;

/**
 * NovaNews — крипто-новости страны/языка из конфига (Google News RSS
 * через наш /api/news). Широкий блок нижнего ряда «кирпичной кладки»,
 * под панелью вывода. Видимостью управляет родитель (NovaDashboard).
 */
const REFRESH_MS = 5 * 60 * 1000;
// Тянем пул с запасом — сколько показать, решает сетка по размеру экрана
const MAX_ITEMS = 40;

// Кэш на уровне модуля — переживает перемонтирования при смене разделов
let cache = { items: null, fetchedAt: 0, key: '' };

// Домен издания → фавиконка через сервис Google (есть у всех, грузится всегда)
const sourceLogo = (sourceUrl) => {
    try {
        const host = new URL(sourceUrl).hostname;
        return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
    } catch (_) {
        return null;
    }
};

export default function NovaNews() {
    const { safeT, siteSettings = {} } = useAppContext();
    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;

    const lang = (siteSettings.currentLanguage || 'en').toLowerCase();
    const country = (siteSettings.selectedFlag || 'us').toUpperCase();
    const key = `${lang}-${country}`;

    const [items, setItems] = useState(cache.key === key ? cache.items : null);
    // Адаптивная плотность: колонки — по ширине, ряды — по высоте контейнера
    const { ref: gridRef, cols, rows } = useFitGrid({
        rowHeight: ROW_H, rowGap: ROW_GAP, minColWidth: MIN_COL_W, colGap: COL_GAP,
        minRows: 2, minCols: 2, maxCols: 5, // не больше 5 колонок даже на ультравайде
    });

    // Относительное время на языке конфига — без ключей переводов
    const relTime = useMemo(() => {
        try { return new Intl.RelativeTimeFormat(lang, { numeric: 'always', style: 'short' }); }
        catch (_) { return new Intl.RelativeTimeFormat('en', { numeric: 'always', style: 'short' }); }
    }, [lang]);
    const formatAgo = (dateStr) => {
        const ts = Date.parse(dateStr);
        if (Number.isNaN(ts)) return '';
        const mins = Math.round((ts - Date.now()) / 60000);
        if (mins > -60) return relTime.format(mins, 'minute');
        const hours = Math.round(mins / 60);
        if (hours > -24) return relTime.format(hours, 'hour');
        return relTime.format(Math.round(hours / 24), 'day');
    };

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            try {
                const res = await fetch(`/api/news?lang=${encodeURIComponent(lang)}&country=${encodeURIComponent(country)}`);
                if (!res.ok) return;
                const json = await res.json();
                const list = (json.items || []).slice(0, MAX_ITEMS).map((n) => ({
                    ...n,
                    logo: sourceLogo(n.sourceUrl),
                }));
                if (!list.length) return;
                cache = { items: list, fetchedAt: Date.now(), key };
                if (!cancelled) setItems(list);
            } catch (_) { /* остаёмся на кэше */ }
        };

        if (cache.key !== key || !cache.items || Date.now() - cache.fetchedAt > REFRESH_MS) load();
        const timer = setInterval(load, REFRESH_MS);
        return () => { cancelled = true; clearInterval(timer); };
    }, [lang, country, key]);

    if (!items || !items.length) return null;

    return (
        <div className={s.wrap}>
            <span className={s.tab}>
                {t('dashboard.newsTitle', 'Новости')}
                <Newspaper size={12} strokeWidth={2} aria-hidden="true" />
            </span>
            <div className={s.card}>
                <div
                    className={s.grid}
                    ref={gridRef}
                    style={{
                        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                        // Ряды растягиваются на всю высоту — без пустого «хвоста».
                        // Число рядов урезаем под реальные данные, чтобы не было
                        // растянутых полупустых рядов
                        gridTemplateRows: `repeat(${Math.max(1, Math.ceil(Math.min(cols * rows, items.length) / cols))}, 1fr)`,
                    }}
                >
                    {items.slice(0, cols * rows).map((n, i) => (
                        <a
                            key={i}
                            href={n.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            // Разделители зависят от фактического числа колонок:
                            // вертикальный — у всех, кроме первой колонки ряда,
                            // горизонтальный — у всех рядов, кроме первого
                            className={`${s.item} ${i % cols !== 0 ? s.vDiv : ''} ${i >= cols ? s.hDiv : ''}`}
                        >
                            {n.logo ? (
                                <img src={n.logo} alt="" className={s.logo} loading="lazy" />
                            ) : (
                                <span className={s.logoFallback} aria-hidden="true">
                                    <Newspaper size={14} strokeWidth={1.9} />
                                </span>
                            )}
                            <span className={s.body}>
                                <span className={s.title}>{n.title}</span>
                                <span className={s.meta}>
                                    <span className={s.source}>{n.source}</span>
                                    {n.publishedAt && (
                                        <>
                                            <span className={s.dot} aria-hidden="true" />
                                            <span className={s.time}>{formatAgo(n.publishedAt)}</span>
                                        </>
                                    )}
                                </span>
                            </span>
                            <ExternalLink size={13} strokeWidth={1.9} className={s.ext} aria-hidden="true" />
                        </a>
                    ))}
                </div>
            </div>
        </div>
    );
}
